import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { combineLatest, distinctUntilChanged, forkJoin, Subscription } from 'rxjs';

import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import {
  CreateDeviceDowntimeRequest,
  DeviceDowntimeImpactPreview,
  DeviceDowntimeItem,
  DeviceDowntimeListSort,
} from '../data-access/downtime.models';
import { DowntimeService } from '../data-access/downtime.service';
import { DowntimeClinicOption, DowntimeFormComponent } from '../components/downtime-form.component';
import { DowntimeImpactSummaryComponent } from '../components/downtime-impact-summary.component';
import { DowntimeQuickViewModalComponent } from '../components/downtime-quick-view-modal.component';
import { DowntimeTableComponent } from '../components/downtime-table.component';
import { AuthService } from '../../../core/auth/auth.service';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { SettingsService } from '../../settings/data-access/settings.service';
import { ClinicOperationalSettingsDto } from '../../settings/data-access/settings.models';
import { DevicesService } from '../../devices/data-access/devices.service';
import { DeviceItem } from '../../devices/data-access/devices.models';
import { DeviceQuickViewModalComponent } from '../../devices/components/device-quick-view-modal.component';
import { ClinicQuickViewModalComponent } from '../../clinics/components/clinic-quick-view-modal.component';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import {
  ErrorBlockComponent,
  FloatingActionFabComponent,
  PageActionModalComponent,
  PageContextGuardComponent,
  PageShellComponent,
  PagedFeedComponent,
} from '@clinic-platform/ui-components';

@Component({
  selector: 'app-downtime-list-page',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CompanyContextRequiredComponent,
    ErrorBlockComponent,
    FloatingActionFabComponent,
    PageContextGuardComponent,
    PageActionModalComponent,
    PagedFeedComponent,
    DowntimeFormComponent,
    DowntimeImpactSummaryComponent,
    DowntimeQuickViewModalComponent,
    DowntimeTableComponent,
    DeviceQuickViewModalComponent,
    ClinicQuickViewModalComponent,
    PageShellComponent,
    TranslatePipe
  ],
  template: `
    <lib-page-shell>
      <lib-page-context-guard [blocked]="companyContextMissing">
        <app-company-context-required />
      </lib-page-context-guard>

      @if (!companyContextMissing) {
        @if (actionErrorText) {
          <lib-error-block variant="error" [message]="(actionErrorText | t) ?? ''" />
        }

        <lib-paged-feed
          [items]="items"
          [collectionTemplate]="downtimeTableTemplate"
          [loading]="isLoading"
          [loadingMore]="isLoadingMore"
          [hasMore]="hasMoreItems"
          [errorText]="errorText"
          emptyText="No downtime records found"
          loadMoreText="Load more downtime"
          loadingMoreText="Loading more downtime..."
          retryText="Retry"
          (loadMore)="loadMoreDowntime()"
          (retry)="refresh()"
        />

        <ng-template #downtimeTableTemplate let-items="items">
          <app-downtime-table
            [items]="items"
            [clinics]="clinicOptions"
            [devices]="deviceOptions"
            [busyItemId]="busyItemId"
            [canModerateRequests]="canModerateRequests"
            [canManageDowntime]="canManageDowntime"
            [canOpenDevice]="canOpenDevice"
            [canOpenClinic]="canOpenClinic"
            (deleteClicked)="onDelete($event)"
            (resolveClicked)="onResolve($event)"
            (approveClicked)="onApprove($event)"
            (rejectClicked)="onReject($event)"
            (deviceOpenClicked)="openDeviceQuickView($event)"
            (clinicOpenClicked)="openClinicQuickView($event)"
            (rowClicked)="openDowntimeQuickView($event)"
          />
        </ng-template>
      }

      @if (isCreateModalOpen) {
        <lib-page-action-modal
          [isOpen]="true"
          title="Create Device Downtime"
          subtitle="Manual maintenance and outage registration"
          width="760px"
          [canClose]="!isCreateSubmitting"
          (close)="closeCreateModal()"
        >

            @if (createPreviewImpact && pendingCreatePayload) {
              <app-downtime-impact-summary
                [affectedAppointmentIds]="createPreviewImpact.affectedAppointmentIds"
                [reachabilityImpact]="createPreviewImpact.reachabilityImpact"
              />

              <div class="downtime-page__preview-actions">
                <button
                  type="button"
                  class="downtime-page__modal-secondary"
                  [disabled]="isCreateSubmitting"
                  (click)="backToCreateForm()"
                >
                  {{ 'Back to edit' | t }}
                </button>
                <button
                  type="button"
                  class="downtime-page__modal-primary"
                  [disabled]="isCreateSubmitting"
                  (click)="confirmCreateDowntime()"
                >
                  {{ (isCreateSubmitting ? 'Creating...' : 'Create downtime') | t }}
                </button>
              </div>
            }

            @if (createErrorText) {
              <lib-error-block variant="error" [message]="createErrorText" />
            }

            @if (!createPreviewImpact) {
              <app-downtime-form
                [isSubmitting]="isCreateSubmitting"
                [errorText]="createErrorText"
                [clinics]="clinicOptions"
                [devices]="deviceOptions"
                [initialValue]="pendingCreatePayload"
                (submitted)="onCreateSubmitted($event)"
              />
            }
        </lib-page-action-modal>
      }

      @if (!companyContextMissing) {
        <lib-floating-action-fab
          [menuTemplate]="downtimeQuickActionsMenu"
          buttonAriaLabel="Open device downtime quick actions"
        />

        <ng-template #downtimeQuickActionsMenu let-close="close">
          <div class="downtime-page__fab-menu">
            @if (canCreateDowntimeRequest) {
              <button
                type="button"
                role="menuitem"
                class="action-menu__item"
                [disabled]="!canOpenCreateModal"
                (click)="openCreateModal(); close()"
              >
                {{ 'Create downtime' | t }}
              </button>
            }
            <button
              type="button"
              role="menuitem"
              class="action-menu__item"
              (click)="openFilterModal(); close()"
            >
              Filters & Sort
            </button>
            <button
              type="button"
              role="menuitem"
              class="action-menu__item"
              (click)="refresh(); close()"
            >
              Refresh
            </button>
          </div>
        </ng-template>
      }

      @if (isFilterModalOpen) {
        <lib-page-action-modal
          [isOpen]="true"
          title="Filters & Sort"
          subtitle="Device, status and sort controls for downtime list"
          width="760px"
          (close)="closeFilterModal()"
        >

            <form
              [formGroup]="filtersForm"
              (submit)="$event.preventDefault(); onApplyFilters(); closeFilterModal()"
              class="downtime-page__modal-filters"
            >
              <div class="downtime-page__filters-grid">
                @if (!isSystemAdmin) {
                  <div class="downtime-page__filters-field">
                    <label for="downtime-filters-clinicId">{{ 'Clinic' | t }}</label>
                    <select id="downtime-filters-clinicId" formControlName="clinicId" (change)="onClinicFilterChanged()">
                      <option value="">{{ 'All' | t }}</option>
                      @for (item of clinicOptions; track item.id) {
                        <option [value]="item.id">{{ item.label }}</option>
                      }
                    </select>
                  </div>
                }

                <div class="downtime-page__filters-field">
                  <label for="downtime-filters-deviceId">{{ 'Device' | t }}</label>
                  <select id="downtime-filters-deviceId" formControlName="deviceId">
                    <option value="">{{ 'slots.allDevices' | t }}</option>
                    @for (item of filteredDeviceOptions; track item.id) {
                      <option [value]="item.id">{{ item.label }}</option>
                    }
                  </select>
                </div>

                <div class="downtime-page__filters-field">
                  <label for="downtime-filters-status">{{ 'common.status' | t }}</label>
                  <select id="downtime-filters-status" formControlName="status">
                    <option value="all">{{ 'All' | t }}</option>
                    @for (status of statuses; track status) {
                      <option [value]="status">{{ downtimeStatusLabel(status) }}</option>
                    }
                  </select>
                </div>

                <label class="downtime-page__checkbox-field">
                  <input type="checkbox" formControlName="pendingFirst" />
                  <span>Pending first</span>
                </label>

                <div class="downtime-page__filters-field">
                  <label for="downtime-filters-sort">Sort</label>
                  <select id="downtime-filters-sort" formControlName="sort">
                    <option value="startTimeDesc">Newest start</option>
                    <option value="startTimeAsc">Oldest start</option>
                    <option value="expectedRepairUntilDesc">Latest repair until</option>
                    <option value="expectedRepairUntilAsc">Earliest repair until</option>
                    <option value="createdAtDesc">Newest created</option>
                    <option value="createdAtAsc">Oldest created</option>
                    <option value="updatedAtDesc">Newest updated</option>
                    <option value="updatedAtAsc">Oldest updated</option>
                    <option value="statusAsc">Status A-Z</option>
                    <option value="statusDesc">Status Z-A</option>
                  </select>
                </div>
              </div>

              <div class="downtime-page__modal-actions">
                <button type="button" class="downtime-page__modal-secondary" (click)="onResetFilters(); closeFilterModal()">
                  {{ 'Reset' | t }}
                </button>
                <button type="submit" class="downtime-page__modal-primary">
                  {{ 'Apply Filters' | t }}
                </button>
              </div>
            </form>
        </lib-page-action-modal>
      }

      @if (isDeviceQuickViewOpen && selectedDeviceId) {
        <app-device-quick-view-modal
          [isOpen]="true"
          [deviceId]="selectedDeviceId"
          title="Device Quick View"
          (close)="closeDeviceQuickView()"
        />
      }

      @if (isClinicQuickViewOpen && selectedClinicId) {
        <app-clinic-quick-view-modal
          [isOpen]="true"
          [clinicId]="selectedClinicId"
          title="Clinic Quick View"
          (close)="closeClinicQuickView()"
        />
      }

      @if (isDowntimeQuickViewOpen && selectedDowntime) {
        <app-downtime-quick-view-modal
          [isOpen]="true"
          [item]="selectedDowntime"
          [clinics]="clinicOptions"
          [devices]="deviceOptions"
          (close)="closeDowntimeQuickView()"
        />
      }
    </lib-page-shell>
  `,
  styles: [`
    .downtime-page__create-button {
      height: 40px;
      border: none;
      border-radius: 8px;
      background: #2563eb;
      color: #ffffff;
      padding: 0 16px;
      cursor: pointer;
      white-space: nowrap;
    }

    .downtime-page__moderation-hint {
      margin-bottom: 12px;
      padding: 10px 12px;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
      background: #eff6ff;
      color: #1d4ed8;
      font-size: 14px;
      font-weight: 500;
    }

    .downtime-page__filters-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 16px;
    }

    .downtime-page__filters-field {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .downtime-page__filters-field label {
      font-size: 13px;
      font-weight: 600;
      color: #374151;
    }

    .downtime-page__filters-field select {
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 12px;
      font-size: 14px;
      box-sizing: border-box;
    }

    .downtime-page__checkbox-field {
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 40px;
      padding-top: 21px;
      color: #374151;
      font-size: 14px;
      font-weight: 600;
      box-sizing: border-box;
    }

    .downtime-page__checkbox-field input {
      width: 16px;
      height: 16px;
      margin: 0;
    }

    .downtime-page__fab-menu-grid {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 8px;
      width: 56px;
    }

    .downtime-page__fab-menu-grid button.downtime-page__fab-action {
      width: 48px;
      min-width: 48px;
      min-height: 48px;
      justify-content: center;
      padding: 0;
      border-radius: 14px;
    }

    .downtime-page__fab-icon {
      position: relative;
      display: inline-flex;
      width: 18px;
      height: 18px;
      color: #0f172a;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .downtime-page__fab-icon--filter::before {
      content: '';
      width: 14px;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 8px solid currentColor;
      position: absolute;
      top: 1px;
    }

    .downtime-page__fab-icon--filter::after {
      content: '';
      position: absolute;
      top: 10px;
      width: 4px;
      height: 6px;
      background: currentColor;
      border-radius: 0 0 2px 2px;
    }

    .downtime-page__modal-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      background: rgba(15, 23, 42, 0.52);
      backdrop-filter: blur(6px);
    }

    .downtime-page__modal {
      width: min(760px, 100%);
      max-height: min(90vh, 960px);
      overflow: auto;
      border-radius: 24px;
      border: 1px solid rgba(226, 232, 240, 0.9);
      background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.97));
      box-shadow: 0 24px 80px rgba(15, 23, 42, 0.22);
      padding: 22px;
      box-sizing: border-box;
    }

    .downtime-page__modal-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 16px;
    }

    .downtime-page__modal-title {
      margin: 0;
      font-size: 24px;
      line-height: 1.1;
      color: #111827;
    }

    .downtime-page__modal-subtitle {
      margin: 8px 0 0;
      color: #6b7280;
      font-size: 14px;
      line-height: 1.5;
    }

    .downtime-page__modal-close {
      width: 40px;
      height: 40px;
      flex-shrink: 0;
      border: 1px solid #dbe2ea;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.92);
      color: #475569;
      font-size: 24px;
      line-height: 1;
      cursor: pointer;
    }

    .downtime-page__modal-filters {
      display: grid;
      gap: 16px;
    }

    .downtime-page__modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
    }

    .downtime-page__preview-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin: 16px 0;
    }

    .downtime-page__modal-secondary,
    .downtime-page__modal-primary {
      min-height: 40px;
      border-radius: 10px;
      padding: 0 14px;
      font-weight: 600;
      cursor: pointer;
    }

    .downtime-page__modal-secondary {
      border: 1px solid #d1d5db;
      background: #ffffff;
      color: #334155;
    }

    .downtime-page__modal-primary {
      border: none;
      background: #2563eb;
      color: #ffffff;
    }

    @media (max-width: 900px) {
      .downtime-page__filters-grid {
        grid-template-columns: 1fr;
      }

      .downtime-page__modal {
        width: min(100%, 760px);
        max-height: min(92vh, 960px);
        padding: 18px;
        border-radius: 20px;
      }
    }

    @media (max-width: 640px) {
      .downtime-page__modal-backdrop {
        padding: 0;
        align-items: flex-end;
      }

      .downtime-page__modal {
        width: 100%;
        max-height: 92vh;
        border-radius: 24px 24px 0 0;
        padding: 16px;
      }

      .downtime-page__modal-head {
        gap: 12px;
      }

      .downtime-page__modal-title {
        font-size: 22px;
      }

      .downtime-page__create-button,
      .downtime-page__modal-secondary,
      .downtime-page__modal-primary {
        width: 100%;
      }

      .downtime-page__modal-actions {
        flex-direction: column;
      }

      .downtime-page__preview-actions {
        flex-direction: column;
      }
    }
  `]
})
export class DowntimeListPageComponent implements OnInit, OnDestroy
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly downtimeService = inject(DowntimeService);
  private readonly settingsService = inject(SettingsService);
  private readonly devicesService = inject(DevicesService);
  private readonly authService = inject(AuthService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly subscriptions = new Subscription();

  protected items: DeviceDowntimeItem[] = [];
  protected isLoading = true;
  protected isLoadingMore = false;
  protected hasMoreItems = false;
  protected errorText: string | null = null;
  protected actionErrorText: string | null = null;
  protected busyItemId: string | null = null;
  protected canModerateRequests = false;
  protected canManageDowntime = false;
  protected canCreateDowntimeRequest = false;
  protected canOpenDevice = false;
  protected canOpenClinic = false;
  protected clinicOptions: DowntimeClinicOption[] = [];
  protected deviceOptions: Array<{ id: string; label: string; clinicId: string }> = [];
  protected isCreateModalOpen = false;
  protected isCreateSubmitting = false;
  protected isReferenceOptionsLoading = false;
  protected isFilterModalOpen = false;
  protected isDeviceQuickViewOpen = false;
  protected isClinicQuickViewOpen = false;
  protected isDowntimeQuickViewOpen = false;
  protected createErrorText: string | null = null;
  protected createPreviewImpact: DeviceDowntimeImpactPreview | null = null;
  protected pendingCreatePayload: CreateDeviceDowntimeRequest | null = null;
  protected isSystemAdmin = false;
  protected selectedGlobalClinicId: string | null = null;
  protected selectedDeviceId: string | null = null;
  protected selectedClinicId: string | null = null;
  protected selectedDowntime: DeviceDowntimeItem | null = null;
  private referenceOptionsLoadedForCompanyId: string | null = null;
  private nextCursor: string | null = null;
  private readonly pageSize = 20;
  protected readonly statuses: Array<'pending_approval' | 'active' | 'resolved' | 'rejected'> = [
    'pending_approval',
    'active',
    'resolved',
    'rejected',
  ];
  protected readonly filtersForm = this.formBuilder.group({
    clinicId: [''],
    deviceId: [''],
    status: ['all'],
    pendingFirst: [true],
    sort: ['startTimeDesc'],
  });
  private appliedFilters: {
    clinicId?: string;
    deviceId?: string;
    status?: 'all' | 'pending_approval' | 'active' | 'resolved' | 'rejected';
    pendingFirst: boolean;
    sort: DeviceDowntimeListSort;
  } = { pendingFirst: true, sort: 'startTimeDesc' };

  protected get filteredDeviceOptions(): Array<{ id: string; label: string; clinicId: string }>
  {
    const clinicId = this.isSystemAdmin
      ? this.selectedGlobalClinicId?.trim() ?? ''
      : this.filtersForm.getRawValue().clinicId?.trim() ?? '';
    if (!clinicId) {
      return this.deviceOptions;
    }

    return this.deviceOptions.filter((item) => item.clinicId === clinicId);
  }

  protected get canOpenCreateModal(): boolean {
    return this.canCreateDowntimeRequest && !this.isReferenceOptionsLoading;
  }

  public ngOnInit(): void
  {
    const role = this.authService.getCurrentRole();
    this.isSystemAdmin = role === 'system_admin';
    this.selectedGlobalClinicId = this.systemAdminContext.getClinicId();
    this.canModerateRequests = role === 'manager' || role === 'company_admin' || role === 'system_admin';
    this.canManageDowntime = role === 'manager' || role === 'company_admin' || role === 'system_admin';
    this.canCreateDowntimeRequest =
      role === 'company_admin' || role === 'manager' || role === 'technician' || role === 'system_admin';
    this.canOpenDevice =
      role === 'system_admin' || role === 'company_admin' || role === 'manager' || role === 'technician' || role === 'reception';
    this.canOpenClinic = this.canOpenDevice;
    if (this.isSystemAdmin) {
      this.subscriptions.add(this.systemAdminContext.companyId$.pipe(distinctUntilChanged()).subscribe((companyId) => {
        this.referenceOptionsLoadedForCompanyId = null;
        if (!companyId?.trim()) {
          this.clinicOptions = [];
          this.deviceOptions = [];
          this.isReferenceOptionsLoading = false;
          return;
        }
        this.ensureReferenceOptionsLoaded();
      }));
      this.subscriptions.add(
        combineLatest([
          this.systemAdminContext.companyId$.pipe(distinctUntilChanged()),
          this.systemAdminContext.clinicId$.pipe(distinctUntilChanged()),
        ]).subscribe(([, clinicId]) => {
          this.selectedGlobalClinicId = clinicId;
          this.ensureReferenceOptionsLoaded();
          this.loadDowntime(this.getEffectiveFilters(), false);
        }),
      );
      return;
    }

    this.ensureReferenceOptionsLoaded();
    this.loadDowntime(this.getEffectiveFilters(), false);
  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();
  }

  protected openCreateModal(): void
  {
    this.createPreviewImpact = null;
    this.pendingCreatePayload = null;
    this.createErrorText = null;
    this.isCreateSubmitting = false;
    this.ensureReferenceOptionsLoaded();
    if (!this.canOpenCreateModal) {
      return;
    }
    this.isCreateModalOpen = true;
  }

  protected closeCreateModal(): void
  {
    if (this.isCreateSubmitting) {
      return;
    }

    this.isCreateModalOpen = false;
    this.createErrorText = null;
    this.createPreviewImpact = null;
    this.pendingCreatePayload = null;
  }

  protected onCreateSubmitted(payload: CreateDeviceDowntimeRequest): void
  {
    if (this.isCreateSubmitting) {
      return;
    }
    this.isCreateSubmitting = true;
    this.createErrorText = null;
    this.createPreviewImpact = null;
    this.pendingCreatePayload = null;

    this.downtimeService.previewImpact(payload).subscribe({
      next: (preview: DeviceDowntimeImpactPreview) =>
      {
        this.createPreviewImpact = preview;
        this.pendingCreatePayload = payload;
        this.isCreateSubmitting = false;
      },
      error: (error: unknown) =>
      {
        console.error('Failed to preview downtime impact', error);
        this.createErrorText = this.resolveCreateErrorText(
          error,
          'Backend rejected device downtime impact preview request.',
        );
        this.isCreateSubmitting = false;
      }
    });
  }

  protected backToCreateForm(): void
  {
    if (this.isCreateSubmitting) {
      return;
    }

    this.createPreviewImpact = null;
    this.createErrorText = null;
  }

  protected confirmCreateDowntime(): void
  {
    if (this.isCreateSubmitting || !this.pendingCreatePayload) {
      return;
    }

    this.createDowntime(this.pendingCreatePayload);
  }

  private createDowntime(payload: CreateDeviceDowntimeRequest): void
  {
    this.isCreateSubmitting = true;
    this.createErrorText = null;

    this.downtimeService.create(payload).subscribe({
      next: (downtime: DeviceDowntimeItem) =>
      {
        this.isCreateSubmitting = false;
        this.closeCreateModal();
        this.loadDowntime(this.getEffectiveFilters(), false);
        this.openDowntimeQuickView(downtime);
      },
      error: (error: unknown) =>
      {
        console.error('Failed to create downtime', error);
        this.createErrorText = this.resolveCreateErrorText(
          error,
          'Backend rejected device downtime creation request.',
        );
        this.isCreateSubmitting = false;
      }
    });
  }

  private resolveCreateErrorText(error: unknown, fallback: string): string
  {
    return resolveHttpErrorMessageKey(error, {
      forbidden: 'error.forbiddenDowntimeCreate',
      badRequestByMessage: {
        'startTime must align to 15-minute slot grid': 'error.downtimeStartSlotGrid',
        'expectedRepairUntil must align to 15-minute slot grid': 'error.downtimeEndSlotGrid',
        'expectedRepairUntil must be greater than startTime': 'error.downtimeEndAfterStart',
        'Invalid downtime time range': 'error.downtimeInvalidTimeRange',
      },
      fallback
    });
  }

  protected openFilterModal(): void
  {
    this.ensureReferenceOptionsLoaded();
    this.isFilterModalOpen = true;
  }

  protected closeFilterModal(): void
  {
    this.isFilterModalOpen = false;
  }

  protected openDeviceQuickView(deviceId: string): void
  {
    const normalized = deviceId.trim();
    if (!this.canOpenDevice || !normalized) {
      return;
    }

    this.selectedDeviceId = normalized;
    this.isDeviceQuickViewOpen = true;
  }

  protected closeDeviceQuickView(): void
  {
    this.isDeviceQuickViewOpen = false;
    this.selectedDeviceId = null;
  }

  protected openClinicQuickView(clinicId: string): void
  {
    const normalized = clinicId.trim();
    if (!this.canOpenClinic || !normalized) {
      return;
    }

    this.selectedClinicId = normalized;
    this.isClinicQuickViewOpen = true;
  }

  protected closeClinicQuickView(): void
  {
    this.isClinicQuickViewOpen = false;
    this.selectedClinicId = null;
  }

  protected openDowntimeQuickView(item: DeviceDowntimeItem): void
  {
    this.selectedDowntime = item;
    this.isDowntimeQuickViewOpen = true;
    this.downtimeService.getById(item.id).subscribe({
      next: (detail: DeviceDowntimeItem) =>
      {
        if (this.isDowntimeQuickViewOpen && this.selectedDowntime?.id === detail.id) {
          this.selectedDowntime = detail;
        }
      },
      error: (error: unknown) =>
      {
        console.error('Failed to load device downtime quick view detail', error);
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenDowntimeAction',
          fallback: 'error.downtimeActionFailed'
        });
      }
    });
  }

  protected closeDowntimeQuickView(): void
  {
    this.isDowntimeQuickViewOpen = false;
    this.selectedDowntime = null;
  }

  protected onDelete(item: DeviceDowntimeItem): void
  {
    this.busyItemId = item.id;
    this.actionErrorText = null;

    this.downtimeService.delete(item.id).subscribe({
      next: () =>
      {
        this.busyItemId = null;
        this.loadDowntime(this.getEffectiveFilters(), false);
      },
      error: (error: unknown) =>
      {
        console.error('Failed to delete downtime', error);
        this.busyItemId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenDowntimeAction',
          fallback: 'error.downtimeActionFailed'
        });
      }
    });
  }

  protected onResolve(item: DeviceDowntimeItem): void
  {
    this.busyItemId = item.id;
    this.actionErrorText = null;

    this.downtimeService.resolve(item.id).subscribe({
      next: () =>
      {
        this.busyItemId = null;
        this.loadDowntime(this.getEffectiveFilters(), false);
      },
      error: (error: unknown) =>
      {
        console.error('Failed to finish downtime', error);
        this.busyItemId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenDowntimeAction',
          fallback: 'error.downtimeActionFailed'
        });
      }
    });
  }

  protected onApprove(item: DeviceDowntimeItem): void
  {
    this.busyItemId = item.id;
    this.actionErrorText = null;

    this.downtimeService.approve(item.id).subscribe({
      next: () =>
      {
        this.busyItemId = null;
        this.loadDowntime(this.getEffectiveFilters(), false);
      },
      error: (error: unknown) =>
      {
        console.error('Failed to approve downtime', error);
        this.busyItemId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenDowntimeAction',
          fallback: 'error.downtimeActionFailed'
        });
      }
    });
  }

  protected onReject(item: DeviceDowntimeItem): void
  {
    this.busyItemId = item.id;
    this.actionErrorText = null;

    this.downtimeService.reject(item.id).subscribe({
      next: () =>
      {
        this.busyItemId = null;
        this.loadDowntime(this.getEffectiveFilters(), false);
      },
      error: (error: unknown) =>
      {
        console.error('Failed to reject downtime', error);
        this.busyItemId = null;
        this.actionErrorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenDowntimeAction',
          fallback: 'error.downtimeActionFailed'
        });
      }
    });
  }

  protected onApplyFilters(): void
  {
    const value = this.filtersForm.getRawValue();
    this.appliedFilters = {
      clinicId: this.isSystemAdmin
        ? this.selectedGlobalClinicId?.trim() || undefined
        : value.clinicId?.trim() || undefined,
      deviceId: value.deviceId?.trim() || undefined,
      status: (value.status?.trim() as 'all' | 'pending_approval' | 'active' | 'resolved' | 'rejected' | '') || 'all',
      pendingFirst: !!value.pendingFirst,
      sort: this.normalizeSort(value.sort),
    };
    this.loadDowntime(this.getEffectiveFilters(), false);
  }

  protected onResetFilters(): void
  {
    this.filtersForm.reset({
      clinicId: this.isSystemAdmin ? (this.selectedGlobalClinicId ?? '') : '',
      deviceId: '',
      status: 'all',
      pendingFirst: true,
      sort: 'startTimeDesc',
    });

    this.appliedFilters = this.isSystemAdmin
      ? { clinicId: this.selectedGlobalClinicId?.trim() || undefined, pendingFirst: true, sort: 'startTimeDesc' }
      : { pendingFirst: true, sort: 'startTimeDesc' };
    this.loadDowntime(this.getEffectiveFilters(), false);
  }

  protected loadMoreDowntime(): void
  {
    if (this.isLoading || this.isLoadingMore || !this.hasMoreItems || !this.nextCursor) {
      return;
    }

    this.loadDowntime(this.getEffectiveFilters(), true);
  }

  protected refresh(): void
  {
    this.loadDowntime(this.getEffectiveFilters(), false);
  }

  protected onClinicFilterChanged(): void
  {
    if (this.isSystemAdmin) {
      return;
    }

    const selectedDeviceId = this.filtersForm.getRawValue().deviceId?.trim() ?? '';
    if (!selectedDeviceId) {
      return;
    }

    const stillAvailable = this.filteredDeviceOptions.some((item) => item.id === selectedDeviceId);
    if (!stillAvailable) {
      this.filtersForm.patchValue({ deviceId: '' }, { emitEvent: false });
    }
  }

  protected downtimeStatusLabel(status: 'pending_approval' | 'active' | 'resolved' | 'rejected'): string {
    switch (status) {
      case 'pending_approval':
        return 'Pending approval';
      case 'active':
        return 'Active';
      case 'resolved':
        return 'Resolved';
      case 'rejected':
        return 'Rejected';
    }
  }

  private getEffectiveFilters(): {
    clinicId?: string;
    deviceId?: string;
    status?: 'all' | 'pending_approval' | 'active' | 'resolved' | 'rejected';
    pendingFirst: boolean;
    sort: DeviceDowntimeListSort;
  } {
    if (!this.isSystemAdmin) {
      return this.ensureDefaultSort(this.appliedFilters);
    }

    return {
      ...this.ensureDefaultSort(this.appliedFilters),
      clinicId: this.selectedGlobalClinicId?.trim() || undefined,
    };
  }

  private loadDowntime(
    filters: {
      clinicId?: string;
      deviceId?: string;
      status?: 'all' | 'pending_approval' | 'active' | 'resolved' | 'rejected';
      pendingFirst: boolean;
      sort: DeviceDowntimeListSort;
    },
    append: boolean,
  ): void
  {
    if (!this.hasRequiredCompanyContext()) {
      this.items = [];
      this.nextCursor = null;
      this.hasMoreItems = false;
      this.errorText = 'error.settingsCompanyContextRequired';
      this.actionErrorText = null;
      this.isLoading = false;
      this.isLoadingMore = false;
      return;
    }

    this.isLoading = !append;
    this.isLoadingMore = append;
    this.errorText = null;
    this.actionErrorText = null;

    this.downtimeService.getPage({
      limit: this.pageSize,
      cursor: append ? this.nextCursor : null,
      sort: filters.sort,
      pendingFirst: filters.pendingFirst,
      filters: {
        ...filters,
        status: filters.status === 'all' ? undefined : filters.status,
      },
    }).subscribe({
      next: (page) =>
      {
        this.items = append ? [...this.items, ...page.items] : page.items;
        this.nextCursor = page.nextCursor;
        this.hasMoreItems = page.hasMore;
        this.isLoading = false;
        this.isLoadingMore = false;
      },
      error: (error: unknown) =>
      {
        console.error('Failed to load downtime', error);
        this.errorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenDowntimeList',
          fallback: 'Backend returned an error while loading device downtime.'
        });
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    });
  }

  private normalizeSort(value: string | null | undefined): DeviceDowntimeListSort {
    switch (value) {
      case 'startTimeAsc':
      case 'expectedRepairUntilDesc':
      case 'expectedRepairUntilAsc':
      case 'createdAtDesc':
      case 'createdAtAsc':
      case 'updatedAtDesc':
      case 'updatedAtAsc':
      case 'statusAsc':
      case 'statusDesc':
        return value;
      case 'startTimeDesc':
      default:
        return 'startTimeDesc';
    }
  }

  private ensureDefaultSort(filters: {
    clinicId?: string;
    deviceId?: string;
    status?: 'all' | 'pending_approval' | 'active' | 'resolved' | 'rejected';
    pendingFirst?: boolean;
    sort?: DeviceDowntimeListSort;
  }): {
    clinicId?: string;
    deviceId?: string;
    status?: 'all' | 'pending_approval' | 'active' | 'resolved' | 'rejected';
    pendingFirst: boolean;
    sort: DeviceDowntimeListSort;
  } {
    return {
      ...filters,
      pendingFirst: filters.pendingFirst ?? true,
      sort: filters.sort ?? 'startTimeDesc',
    };
  }

  private loadReferenceOptions(): void
  {
    if (!this.hasRequiredCompanyContext()) {
      this.clinicOptions = [];
      this.deviceOptions = [];
      this.isReferenceOptionsLoading = false;
      return;
    }

    this.isReferenceOptionsLoading = true;

    forkJoin({
      clinics: this.settingsService.getClinicOperationalSettings(),
      devices: this.devicesService.getList(),
    }).subscribe({
      next: ({ clinics, devices }) =>
      {
        this.clinicOptions = clinics.map((item: ClinicOperationalSettingsDto) => ({
          id: item.clinicId,
          label: item.clinicName,
          workingDayStart: item.workingDayStart,
          workingDayEnd: item.workingDayEnd,
          slotDurationMinutes: item.effectiveSlotDurationMinutes,
        }));

        this.deviceOptions = devices.map((item: DeviceItem) => ({
          id: item.id,
          clinicId: item.clinicId,
          label:
            [item.brand, item.model, item.serialNumber]
              .map((part) => part?.trim())
              .filter((part): part is string => !!part)
              .join(' / ') || this.readableId(item.id)
        }));
        this.isReferenceOptionsLoading = false;
      },
      error: () =>
      {
        this.clinicOptions = [];
        this.deviceOptions = [];
        this.isReferenceOptionsLoading = false;
      }
    });
  }

  private ensureReferenceOptionsLoaded(): void
  {
    const companyId = this.isSystemAdmin
      ? this.systemAdminContext.getCompanyId()?.trim() || null
      : 'current-company';

    if (this.referenceOptionsLoadedForCompanyId === companyId) {
      return;
    }

    this.referenceOptionsLoadedForCompanyId = companyId;
    this.loadReferenceOptions();
  }

  private readableId(value: string | null | undefined): string
  {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (normalized.length > 12) {
      return `${normalized.slice(0, 8)}...`;
    }

    return normalized;
  }

  private hasRequiredCompanyContext(): boolean {
    if (!this.isSystemAdmin) {
      return true;
    }

    return !!this.systemAdminContext.getCompanyId()?.trim();
  }

  protected get companyContextMissing(): boolean {
    return !this.hasRequiredCompanyContext();
  }
}
